import { localIsoDate } from '../myTasks/types';
import { useSetDealOutcome } from '../tracker/dealOutcomes';
import { useUpdateDeal } from './hooks';
import type { DropIntent } from './kanbanColumns';
import type { Deal, Pipeline } from './types';

/**
 * One place where a DropIntent becomes a write.
 *
 * Lifted out of DealKanban on 8 September so the list's dropdown and the
 * drawer's could move a deal the same way the board does. Three screens, one
 * function: the board decided this in dropIntent and this applies it, and
 * neither the list nor the drawer gets to re-decide.
 *
 * `reopen` is two facts in one move — the deal is open again AND it belongs at
 * this stage — so it writes both, in that order. Anything less leaves a deal
 * that is closed and sitting in a working column, which is the disagreement
 * the whole design exists to prevent.
 */
export function useMoveDeal(pipeline: Pipeline) {
  const updateDeal = useUpdateDeal(pipeline);
  const setOutcome = useSetDealOutcome();

  return function move(deal: Deal, intent: DropIntent, onError: (cause: unknown) => void) {
    switch (intent.write) {
      case 'stage':
        updateDeal.mutate(
          { dealId: deal.id, changes: { stage_id: intent.stageId === '' ? null : intent.stageId } },
          { onError },
        );
        return;
      case 'outcome':
        setOutcome.mutate(
          { dealId: deal.id, outcome: intent.outcome, closedOn: localIsoDate(new Date()) },
          { onError },
        );
        return;
      case 'reopen':
        setOutcome.mutate(
          { dealId: deal.id, outcome: 'open' },
          {
            onError,
            onSuccess: () => {
              updateDeal.mutate(
                {
                  dealId: deal.id,
                  changes: { stage_id: intent.stageId === '' ? null : intent.stageId },
                },
                { onError },
              );
            },
          },
        );
        return;
      case 'nothing':
        return;
    }
  };
}
